import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Typography } from '@mui/material';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import MemoriesGuestGate from '../components/MemoriesGuestGate';
import { DOCUMENT, PAGE_CONTAINER_MAX_WIDTH, documentPageContainerSx, documentSolidButtonSx } from '../constants/documentTheme';
import { memoriesPath } from '../utils/memoriesHost';
import { memoriesAuthEntryUrls } from '../utils/memoriesAuthBridge';

const MemoriesJoin = () => {
  const { t } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const isMemberInvite = invite?.kind === 'member' || Boolean(invite?.requires_login);
  const joinReturnPath = memoriesPath(`j/${code}`);
  const joinReturnUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${joinReturnPath}`
    : joinReturnPath;
  const authUrls = useMemo(() => memoriesAuthEntryUrls(joinReturnUrl), [joinReturnUrl]);

  useEffect(() => {
    api.get(`/memories/invites/${code}`).then(({ data }) => {
      if (data.invite?.kind === 'view') {
        navigate(memoriesPath(`v/${code}`), { replace: true });
        return;
      }
      setInvite(data.invite);
    }).catch((err) => {
      setError(err.response?.data?.error || t('memories.inviteMissing'));
    });
  }, [code, navigate, t]);

  const acceptMemberInvite = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post(`/memories/invites/${code}/accept`);
      navigate(memoriesPath(`e/${data.slug}`), { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || t('memories.joinFailed'));
    } finally {
      setSaving(false);
    }
  };

  const goToAlbum = (data) => {
    if (data?.slug) {
      navigate(memoriesPath(`e/${data.slug}`), { replace: true });
    }
  };

  return (
    <Box sx={{ backgroundColor: DOCUMENT.canvas, minHeight: '100vh', pb: 8 }}>
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, pt: 8 }}>
        <Typography sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.accent }}>
          {t('memories.brand')}
        </Typography>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 40, color: DOCUMENT.ink, mt: 1 }}>
          {invite?.name || t('memories.title')}
        </Typography>
        {invite && (
          <Typography sx={{ letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 12, color: DOCUMENT.ink3, mt: 1, mb: 4 }}>
            {t('memories.hostLine', { host: invite.host })} · {invite.member_count} {t('memories.members')} · {invite.frame_count} {t('memories.frames')}
          </Typography>
        )}
        {error && <Typography sx={{ color: '#9C3B2A', mb: 2 }}>{error}</Typography>}

        {isMemberInvite && (
          <Box sx={{ border: `1px solid ${DOCUMENT.rule}`, backgroundColor: DOCUMENT.surface, p: 2.5, mb: 2 }}>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, color: DOCUMENT.ink, mb: 1 }}>
              {t('memories.acceptInviteTitle')}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14, mb: 2 }}>
              {invite?.email
                ? t('memories.acceptInviteBodyEmail', { email: invite.email })
                : t('memories.acceptInviteBody')}
            </Typography>
            {user ? (
              <Button
                type="button"
                onClick={acceptMemberInvite}
                disabled={saving}
                sx={{
                  ...documentSolidButtonSx,
                  '&.Mui-disabled': { backgroundColor: DOCUMENT.oxblood, color: DOCUMENT.ivory, opacity: 0.45 },
                }}
              >
                {saving ? t('common.loading') : t('memories.acceptInvite')}
              </Button>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Button
                  component={authUrls.login.startsWith('http') ? 'a' : RouterLink}
                  href={authUrls.login.startsWith('http') ? authUrls.login : undefined}
                  to={authUrls.login.startsWith('http') ? undefined : authUrls.login}
                  sx={{ ...documentSolidButtonSx, textAlign: 'center', width: '100%', py: 1.25 }}
                >
                  {t('memories.signInToAccept')}
                </Button>
                <Button
                  component={authUrls.register.startsWith('http') ? 'a' : RouterLink}
                  href={authUrls.register.startsWith('http') ? authUrls.register : undefined}
                  to={authUrls.register.startsWith('http') ? undefined : authUrls.register}
                  sx={{
                    textAlign: 'center',
                    width: '100%',
                    backgroundColor: '#fff',
                    color: DOCUMENT.ink2,
                    border: `1px solid ${DOCUMENT.rule}`,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    py: 1.25,
                    borderRadius: '2px',
                  }}
                >
                  {t('memories.createAccountToAccept')}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {!isMemberInvite && invite && (
          <MemoriesGuestGate
            inviteCode={code}
            returnUrl={joinReturnUrl}
            showGuestForm={Boolean(invite.allow_guest_uploads)}
            onJoined={goToAlbum}
          />
        )}
      </Container>
    </Box>
  );
};

export default MemoriesJoin;
